import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Film, Loader2, Trash2, Copy, MoreVertical, X } from 'lucide-react';
import { projectsApi } from '@/services/api/projects';
import type { Project } from '@/types/project';

export function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);

  async function load() {
    try {
      setLoading(true);
      const p = await projectsApi.list();
      setProjects(p);
    } catch (e: any) {
      setError(e?.message ?? '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleDelete(id: string) {
    if (!confirm('このプロジェクトを削除しますか?')) return;
    await projectsApi.delete(id);
    setMenuId(null);
    await load();
  }

  async function handleDuplicate(id: string) {
    await projectsApi.duplicate(id);
    setMenuId(null);
    await load();
  }

  return (
    <div className="px-4 py-4 max-w-xl mx-auto">
      <div className="mb-4">
        <h1 className="text-xl font-semibold">プロジェクト</h1>
        <p className="text-xs text-slate-400">保存済みのプロジェクト</p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> 読み込み中…
        </div>
      )}
      {error && <div className="card text-red-400 text-sm">{error}</div>}

      {!loading && !error && projects.length === 0 && (
        <div className="card text-center py-10">
          <Film className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm mb-4">
            まだプロジェクトがありません
          </p>
          <Link to="/editor/new" className="btn-primary">
            <Plus className="h-4 w-4" />
            最初のプロジェクトを作成
          </Link>
        </div>
      )}

      <div className="space-y-2">
        {projects.map((p) => (
          <div
            key={p.id}
            className="relative rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden"
          >
            <Link
              to={`/editor/${p.id}`}
              className="block px-4 py-3 pr-12 min-h-[72px] active:bg-slate-800"
            >
              <h3 className="font-medium text-sm truncate mb-1">{p.title}</h3>
              <div className="text-[11px] text-slate-500 flex items-center gap-2">
                <span>
                  {new Date(p.updatedAt).toLocaleDateString('ja-JP', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span>·</span>
                <StatusBadge status={p.status} />
                {p.durationSec !== null && (
                  <>
                    <span>·</span>
                    <span>{p.durationSec}秒</span>
                  </>
                )}
              </div>
            </Link>
            <button
              onClick={() => setMenuId(menuId === p.id ? null : p.id)}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 active:bg-slate-800"
              aria-label="メニュー"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Action sheet */}
      {menuId && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setMenuId(null)}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-slate-800 bg-slate-900 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            role="dialog"
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-700" />
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-slate-400">アクション</div>
              <button
                onClick={() => setMenuId(null)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800"
                aria-label="閉じる"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => handleDuplicate(menuId)}
                className="btn-secondary w-full"
              >
                <Copy className="h-4 w-4" />
                複製
              </button>
              <button
                onClick={() => handleDelete(menuId)}
                className="btn-ghost w-full text-red-400"
              >
                <Trash2 className="h-4 w-4" />
                削除
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Project['status'] }) {
  const map: Record<Project['status'], { label: string; cls: string }> = {
    draft: { label: '下書き', cls: 'text-slate-400' },
    rendering: { label: '生成中', cls: 'text-amber-400' },
    completed: { label: '完了', cls: 'text-emerald-400' },
    failed: { label: '失敗', cls: 'text-red-400' },
  };
  const info = map[status];
  return <span className={info.cls}>{info.label}</span>;
}
